import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { red, grey } from '@mui/material/colors';
import { Button, Container, Dialog, Typography, CircularProgress, Box, FormGroup, Checkbox, Accordion, AccordionSummary, AccordionDetails, Alert } from '@mui/material';
import { getPermissions, updatePermissions } from '../../../api/userApi';
import { notification } from '../../../utils/Notifications';
import useLogout from '../../../hooks/auth/authHook.js';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { usePermissions } from '../../../hooks/permissions/usePermissions.js';

UsersPermissionsDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
};

export default function UsersPermissionsDialog({ onClose, open, user, fetchUsers }) {
  const [logout] = useLogout();

  const [allSelected, setAllSelected] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isUpdatingPermissions, setIsUpdatingPermissions] = useState(false);
  const [, setExpandedAccordions] = useState([]);

  const { hasAllPermissions } = usePermissions();

  const canEdit = hasAllPermissions(["usr_per_e", "usr_per_v", 'usr_vw']);

  // 🔧 Función auxiliar para desactivar padres sin hijos activos
  const deactivateParentsWithoutActiveChildren = (permissionsList) => {
    let updated = [...permissionsList];
    let hasChanges = true;
    
    // Iterar hasta que no haya más cambios (para manejar casos complejos de dependencias)
    while (hasChanges) {
      hasChanges = false;
      
      for (let i = 0; i < permissions.length; i++) {
        const parentPerm = permissions[i];
        if (parentPerm.permissions && parentPerm.permissions.length > 0) {
          const parentAbbr = parentPerm.permission_abbreviation;
          const childAbbrs = parentPerm.permissions.map(sp => sp.permission_abbreviation);
          const anyChildSelected = updated.some(id => childAbbrs.includes(id));
          
          // Si el padre está seleccionado pero no tiene hijos activos, desactivarlo
          if (updated.includes(parentAbbr) && !anyChildSelected) {
            updated = updated.filter(id => id !== parentAbbr);
            hasChanges = true;
            
            // Si se desactiva un padre, también desactivar los permisos que dependen de él
            const dependentPermissions = reverseDependencies[parentAbbr] || [];
            for (let j = 0; j < dependentPermissions.length; j++) {
              const depPerm = dependentPermissions[j];
              if (updated.includes(depPerm)) {
                updated = updated.filter(id => id !== depPerm);
                hasChanges = true;
              }
            }
          }
        }
      }
    }
    
    return updated;
  };

  const permissionDependencies = {
    'col_asl': ['vw_pel', 'lot_vw'],
    'vw_pel': ['lot_vw'],
    'vw_asl': ['lot_vw'],
    
    'cli_vwi': ['cli_vw'],
    'cli_adb': ['cli_vwi', 'cli_vw', 'cli_vwb'],
    'cli_rmb': ['cli_vwi', 'cli_vw', 'cli_vwb'],
    'cli_ed_sett': ['cli_vwi', 'cli_vw'],
    'cli_adc': ['cli_vwi', 'cli_vw', 'cli_vwc'],
    'cli_rmc': ['cli_vwi', 'cli_vw', 'cli_vwc'],
    'cli_vwc': ['cli_vwi', 'cli_vw'],
    'cli_vwb': ['cli_vwi', 'cli_vw'],
    'cli_ed_d': ["cli_ed_sett", "cli_ed_d", "cli_vw", "cli_vwi"],
    
    'rte_asg': ["rte_vw", "rte_pac_vw"],
    //'rte_vw': ["dgu_vw"], //
    'rte_vwd': ["rtw_vw"],
    'rte_pac_vw': ["rtw_vw"],
    
    'trk_vwg': ["trk_vw"],
    'trk_vw': ["trk_vwg"],
    'trk_eg': ['trk_vw','trk_vwg'],
    'trk_inc': ['trk_vw','trk_vwg'],
    
    'usr_eg': ['usr_vw', 'usr_vwd', 'usr_vwg'],
    'usr_del': ['usr_vw'],
    'usr_chp': ['usr_vw'],
    'usr_vwg': ['usr_vw'],
    'usr_vwa': ['usr_vw'],
    'usr_vwd': ['usr_vw'],
    'usr_vw_d': ['usr_vw'],
    'usr_ea': ['usr_vw', 'usr_vwd', 'usr_vwa'],
    'usr_ra': ['usr_vw', 'usr_vwd', 'usr_vw_d'],
    'usr_crd': ['usr_vw', 'usr_vw_d'],
    'usr_crg': ['usr_vw', 'usr_vwg'],
    'usr_cra': ['usr_vw', 'usr_vwa'],
    'usr_per_v': ['usr_vw', 'usr_vwa'],
    'usr_per_e': ['usr_per_v', 'usr_vw', 'usr_vwa'],

    'tkt_ups': ['tkt_ovw', 'tkt_vw'],
    'tkt_ovw': ['tkt_vw'],
    
    'cut_cash_cls': ['cut_vw', 'cut_ovw'],
    'cut_ow': ['cut_ovw'],
    'cut_owv': ['cut_vw'],
  
    'dgu_del': ['dgu_vw', 'dgu_ovw'],
    'dgu_ovw': ['dgu_vw'],
    
    'cat_vw': ['cat_ovw'],
    'cat_crt': ['cat_vw', 'cat_ovw'],
    'cat_upd': ['cat_vw', 'cat_ovw'],
    'cat_ups': ['cat_vw', 'cat_ovw'],
    'cat_ovw': ['cat_vw'],
    
    'up_loc_g': ['rte_pac_vw', 'rte_vw'],
    'up_loc_b': ['vw_pel', 'lot_vw'],

    'rte_inc_vw': ['rte_vw'],
  };

  // Mapeo inverso para encontrar qué permisos dependen de un permiso dado
  const reverseDependencies = {};
  Object.entries(permissionDependencies).forEach(([mainPerm, deps]) => {
    deps.forEach(dep => {
      if (!reverseDependencies[dep]) {
        reverseDependencies[dep] = [];
      }
      reverseDependencies[dep].push(mainPerm);
    });
  });

  const handleGetPermissions = useCallback(() => {
    if (isLoadingPermissions) {
      return;
    }
    setIsLoadingPermissions(true);
    getPermissions()
      .then((response) => {
        if (response.data.status_Code === 200) {
          setPermissions(response.data.permissions);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener los permisos",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({ text: "Error de conexión, revisa tu conexión a internet", type: "error" });
      })
      .finally(() => {
        setIsLoadingPermissions(false);
      });
  }, []);

  const handleToggleAll = () => {
    if (!canEdit) return;

    if (allSelected) {
      // 🔴 Deseleccionar todos
      setSelectedPermissions([]);
      setAllSelected(false);
    } else {
      // 🟢 Seleccionar todos
      const allAbbrs = permissions.flatMap(p => [
        p.permission_abbreviation,
        ...(p.permissions?.map(sp => sp.permission_abbreviation) || [])
      ]);
      setSelectedPermissions(allAbbrs);
      setAllSelected(true);
    }
  };

  useEffect(() => {
    if (open) {
      const permissionsChecked = user.permissions?.map(p => p.permission_abbreviation) || [];
      setSelectedPermissions(permissionsChecked);
      handleGetPermissions();
    } else {
      setSelectedPermissions([]);
    }
  }, [open, user, handleGetPermissions]);

  const handleSaveAdminClick = () => {
    if (!canEdit) return;
    updateUserPermissions();
    onClose();
  };

  const handleCloseAdminClick = () => {
    handleDialogClose();
  };

  const buttonStyles = {
    width: '120px',
    borderRadius: '0.5rem',
    height: '38px',
  };

  const activeButtonStyles = {
    ...buttonStyles,
    backgroundColor: red['A400'],
    color: 'white',
    '&:hover': {
      backgroundColor: red['A700'],
    },
  };

  const inactiveButtonStyles = {
    ...buttonStyles,
    backgroundColor: grey[500],
    color: grey[300],
    '&:hover': {
      backgroundColor: grey[500],
    },
  };

  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  const handlePermissionChange = (permissionId, parentId = null) => {
    if (!canEdit) return;

    setSelectedPermissions((prev) => {
      const isSelected = prev.includes(permissionId);
      let updated = [...prev];

      // 🔹 Si está seleccionado, lo quitamos
      if (isSelected) {
        updated = updated.filter((id) => id !== permissionId);

        // Si es padre → quitar también subpermisos y colapsar
        const parentPerm = permissions.find((p) => p.permission_abbreviation === permissionId);
        if (parentPerm && parentPerm.permissions?.length > 0) {
          const subAbbrs = parentPerm.permissions.map((sp) => sp.permission_abbreviation);
          updated = updated.filter((id) => !subAbbrs.includes(id));
          toggleAccordion(permissionId, false); // 👈 colapsar
        }

        // Si es subpermiso → revisar si el padre queda sin hijos seleccionados
        if (parentId) {
          const parent = permissions.find((p) => p.permission_abbreviation === parentId);
          const childAbbrs = parent?.permissions?.map((sp) => sp.permission_abbreviation) || [];
          const anyChildSelected = updated.some((id) => childAbbrs.includes(id));
          if (!anyChildSelected) {
            updated = updated.filter((id) => id !== parentId);
          }
        }

        // 🔴 LÓGICA DE DEPENDENCIAS: Si se desactiva un permiso, desactivar también los que dependen de él
        const dependentPermissions = reverseDependencies[permissionId] || [];
        dependentPermissions.forEach(depPerm => {
          updated = updated.filter((id) => id !== depPerm);
        });

        // 🔴 LÓGICA ADICIONAL: Revisar todos los padres para desactivar los que no tengan hijos activos
        // (después de las dependencias para considerar todos los cambios)
        updated = deactivateParentsWithoutActiveChildren(updated);

        return updated;
      } 
      
      // 🔹 Si no está seleccionado, lo agregamos
      else {
        // 🟢 LÓGICA DE DEPENDENCIAS: Si se activa un permiso, activar también sus dependencias
        const requiredDependencies = permissionDependencies[permissionId] || [];
        const newPermissions = [permissionId, ...requiredDependencies];

        // Si es padre → marcar también subpermisos y expandir
        const parentPerm = permissions.find((p) => p.permission_abbreviation === permissionId);
        if (parentPerm && parentPerm.permissions?.length > 0) {
          const subAbbrs = parentPerm.permissions.map((sp) => sp.permission_abbreviation);
          toggleAccordion(permissionId, true); // 👈 expandir
          newPermissions.push(...subAbbrs);
        }

        // Si es subpermiso → marcar padre también
        if (parentId && !updated.includes(parentId)) {
          newPermissions.push(parentId);
        }

        return [...new Set([...updated, ...newPermissions])];
      }
    });
  };

  const handleToggleSubPermissions = (mainPerm) => {
    if (!canEdit) return;

    setSelectedPermissions(prev => {
      const subAbbrs = mainPerm.permissions.map(sp => sp.permission_abbreviation);
      const allSelectedInGroup = subAbbrs.every(id => prev.includes(id));

        // Si ya todos están seleccionados → deseleccionar todos
        if (allSelectedInGroup) {
          let updated = prev.filter(id => !subAbbrs.includes(id));
          
          // 🔴 LÓGICA DE DEPENDENCIAS: Si se desactivan subpermisos, desactivar también los que dependen de ellos
          subAbbrs.forEach(subPerm => {
            const dependentPermissions = reverseDependencies[subPerm] || [];
            dependentPermissions.forEach(depPerm => {
              updated = updated.filter((id) => id !== depPerm);
            });
          });
          
          // 🔴 LÓGICA ADICIONAL: Desactivar padres sin hijos activos
          updated = deactivateParentsWithoutActiveChildren(updated);
          
          return updated;
        }

      // Si no → seleccionarlos todos junto al padre
      let newPermissions = [mainPerm.permission_abbreviation, ...subAbbrs];
      
      // 🟢 LÓGICA DE DEPENDENCIAS: Activar dependencias de cada subpermiso
      subAbbrs.forEach(subPerm => {
        const requiredDependencies = permissionDependencies[subPerm] || [];
        newPermissions.push(...requiredDependencies);
      });
      
      return [...new Set([...prev, ...newPermissions])];
    });
  };

  const updateUserPermissions = () => {

    if(isUpdatingPermissions) { return; }
    setIsUpdatingPermissions(true);

    updatePermissions(user.id, selectedPermissions)
      .then((response) => {
        if (response.data.status_Code === 200) {
          notification({ text: "Permisos actualizados", type: "success" });
          fetchUsers();
          handleDialogClose();
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al actualizar los permisos",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({text: 'Error de conexión, revisa tu conexión a internet', type: 'error'});
      })
      .finally(() => {
        setIsUpdatingPermissions(false);
      });
  }

  const toggleAccordion = (abbr, expand = null) => {
    setExpandedAccordions(prev => {
      const isExpanded = prev.includes(abbr);
      if (expand === true) return [...new Set([...prev, abbr])];
      if (expand === false) return prev.filter(id => id !== abbr);
      return isExpanded ? prev.filter(id => id !== abbr) : [...prev, abbr];
    });
  };

  return (
    <Dialog onClose={handleDialogClose} open={open} PaxperProps={{ style: { borderRadius: '16px', maxWidth: '800px' } }} maxWidth={true}>
      <Container style={{ padding: 26, maxWidth: '700px' }}>
        <Typography component="div" variant="h5" style={{ textAlign: 'start', fontWeight: 'bold', color: grey[800], marginBottom: 12 }}>
          Permisos de usuario
        </Typography>

        {isLoadingPermissions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress style={{ color: red['A400'] }} />
          </Box>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={handleToggleAll}
              disabled={!canEdit}
              size="small"
              sx={{
                mb: 2,
                padding: '4px 12px',
                color: red['A400'],
                borderColor: red['A400'],
                backgroundColor: red[50],
                '&:hover': {
                  borderColor: red['A700'],
                  backgroundColor: red[100],
                },
                textTransform: 'none',
              }}
            >
              {allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
            </Button>

            {permissions.map(mainPerm => {
              const hasSubPermissions = mainPerm.permissions && mainPerm.permissions.length > 0;
              const allSubSelected =
                hasSubPermissions &&
                mainPerm.permissions.every(sp =>
                  selectedPermissions.includes(sp.permission_abbreviation)
                );

              // 🟢 Si NO tiene subpermisos → render simple sin Accordion
              if (!hasSubPermissions) {
                return (
                  <Box
                    key={mainPerm.permission_id}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      py: 0.5,
                      px: 0,
                    }}
                  >
                    <Checkbox
                      checked={selectedPermissions.includes(mainPerm.permission_abbreviation)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handlePermissionChange(mainPerm.permission_abbreviation);
                      }}
                      disabled={!canEdit}
                      sx={{
                        color: grey[400],
                        '&.Mui-checked': { color: red['A400'] },
                      }}
                    />
                    <Typography fontWeight="bold">
                      {mainPerm.permission_description}
                    </Typography>
                  </Box>
                );
              }

              return (
                <Accordion
                  key={mainPerm.permission_id}
                  disableGutters
                  elevation={0}
                  sx={{
                    margin: 0,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      py: 0.5,
                      px: 0,
                      '& .MuiAccordionSummary-content': {
                        margin: 0,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      },
                    }}
                    onClick={(e) => {
                      if (e.target.closest('button')) e.stopPropagation();
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Checkbox
                        checked={selectedPermissions.includes(mainPerm.permission_abbreviation)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handlePermissionChange(mainPerm.permission_abbreviation);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={!canEdit}
                        sx={{
                          color: grey[400],
                          '&.Mui-checked': { color: red['A400'] },
                        }}
                      />
                      <Typography fontWeight="bold">
                        {mainPerm.permission_description}
                      </Typography>
                    </Box>

                    {canEdit && mainPerm.permissions?.length > 0 && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleSubPermissions(mainPerm);
                        }}
                        sx={{
                          padding: '4px 12px',
                          color: red['A400'],
                          borderColor: red['A400'],
                          backgroundColor: red[50],
                          marginLeft: '18px',
                          fontSize: '0.8rem',
                          '&:hover': {
                            borderColor: red['A700'],
                            backgroundColor: red[100],
                          },
                          textTransform: 'none',
                        }}
                      >
                        {allSubSelected ? 'Deseleccionar subpermisos' : 'Seleccionar subpermisos'}
                      </Button>
                    )}
                  </AccordionSummary>

                  <AccordionDetails sx={{ bgcolor: 'grey.100', borderRadius: 1, py: 1, px: 2 }}>
                    <FormGroup>
                      {mainPerm.permissions.map(subPerm => (
                        <Box key={subPerm.permission_id} display="flex" alignItems="center" gap={1} ml={6}>
                          <Checkbox
                            checked={selectedPermissions.includes(subPerm.permission_abbreviation)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handlePermissionChange(
                                subPerm.permission_abbreviation,
                                mainPerm.permission_abbreviation
                              );
                            }}
                            onClick={(e) => e.stopPropagation()}
                            disabled={!canEdit}
                            sx={{
                              color: grey[400],
                              '&.Mui-checked': { color: red['A400'] },
                            }}
                          />
                          <Typography sx={{ userSelect: 'none' }}>
                             {subPerm.permission_description}
                          </Typography>
                        </Box>
                      ))}
                    </FormGroup>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {/* Alert para usr_vwd sin permisos requeridos */}
            {selectedPermissions.includes('usr_vw') && 
             !selectedPermissions.some(perm => ['usr_vw_d', 'usr_vwa', 'usr_vwg'].includes(perm)) && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mt: 2, 
                  mb: 2,
                  backgroundColor: '#fff3cd',
                  borderColor: '#ffeaa7',
                  color: '#856404'
                }}
              >
                Para poder ver los usuarios, debe tener al menos uno de los siguientes permisos:
                <ul>
                  <li>Permiso para ver los usuarios administradores</li>
                  <li>Permiso para ver los repartidores</li>
                  <li>Permiso para ver los usuarios sucursal</li>
                </ul>
              </Alert>
            )}
          </>
        )}

        <Container style={{ display: 'flex', justifyContent: 'end', width: '100%', padding: 0, marginTop: 36, gap: 12 }}>
          <Button
            variant="standard"
            color="primary"
            onClick={handleCloseAdminClick}
            style={{ width: '120px', borderRadius: '0.5rem', height: '38px', color: red['A400'], backgroundColor: red['50'] }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAdminClick}
            disabled={selectedPermissions.includes('usr_vw') && 
                     !selectedPermissions.some(perm => ['usr_vw_d', 'usr_vwa', 'usr_vwg'].includes(perm))}
            sx={isLoadingPermissions ? inactiveButtonStyles : activeButtonStyles}
          >
            {isUpdatingPermissions ? <CircularProgress size={24} /> : 'Guardar'}
          </Button>
        </Container>
      </Container>
    </Dialog>
  );
}